import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  buildFishSpeciesSuggestions,
  getTypeOfFishingOptionsForWay,
  isLureFishingType,
} from "../../../utils/catchMetadataOptions";
import { getFishSpeciesValidationErrorKey } from "../../../utils/fishSpeciesValidation";
import {
  buildSizeValue,
  buildWeightValue,
  normalizeCoords,
} from "../../CreatePostModal/catch-form/CatchFormValidation";
import {
  getReelSuggestions,
  getRodSuggestions,
} from "../../CreatePostModal/catch-form/catchFormGearHelpers";
import useCatchLocationState from "../../CreatePostModal/catch-form/useCatchLocationState";
import { buildEditPostInitialState } from "./editPostPayloadBuilder";

const hookTypeToSpec = (hookType, hookSize) => {
  const normalizedType = String(hookType || "").toLowerCase();
  const normalizedSize = String(hookSize || "");
  if (!normalizedSize) return "";
  if (normalizedType.includes("treble")) return `treble|${normalizedSize}`;
  if (normalizedType.includes("single")) return `single|${normalizedSize}`;
  return normalizedSize;
};

const specToHookFields = (hookSpec) => {
  const [selectedHookType, selectedHookSize] = String(hookSpec || "").split("|");
  return {
    hookType:
      selectedHookType === "single"
        ? "Single Hook"
        : selectedHookType === "treble"
          ? "Treble Hook"
          : "",
    hookSize: selectedHookSize || "",
  };
};
const noop = () => {};

export default function useEditCatchPostFormState(
  post,
  resources = {},
  mapPickSelection = {}
) {
  const { t } = useTranslation();
  const initialState = useMemo(() => buildEditPostInitialState(post), [post]);
  const initialCatch = initialState.catch;
  const pickedCoordinates = mapPickSelection?.pickedCoordinates || "";
  const normalizedPickedCoordinates = useMemo(
    () => normalizeCoords(pickedCoordinates),
    [pickedCoordinates]
  );
  const [caption, setCaption] = useState(initialState.caption);
  const [rating, setRating] = useState(initialCatch.rating);
  const [showFeedback, setShowFeedback] = useState(Boolean(initialCatch.feedback));
  const [feedback, setFeedback] = useState(initialCatch.feedback);
  const [fishInput, setFishInput] = useState(initialCatch.fish);
  const [fishSuggestions, setFishSuggestions] = useState([]);
  const [size, setSize] = useState(initialCatch.size);
  const [weight, setWeight] = useState(initialCatch.weight);
  const [sizeUnit, setSizeUnit] = useState("cm");
  const [sizeFeet, setSizeFeet] = useState("");
  const [sizeInches, setSizeInches] = useState("");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [weightLb, setWeightLb] = useState("");
  const [waterType, setWaterType] = useState(initialCatch.waterType || "all");
  const [wayOfFishing, setWayOfFishing] = useState(initialCatch.wayOfFishing);
  const [typeOfFishing, setTypeOfFishing] = useState(initialCatch.typeOfFishing);
  const [typeOfFishingOptions, setTypeOfFishingOptions] = useState([]);
  const [style, setStyle] = useState(initialCatch.style);
  const [styleOptions, setStyleOptions] = useState([]);
  const [technique, setTechnique] = useState(initialCatch.technique);
  const [techniqueOptions, setTechniqueOptions] = useState([]);
  const [rod, setRod] = useState(initialCatch.rod);
  const [reel, setReel] = useState(initialCatch.reel);
  const [braid, setBraid] = useState(initialCatch.braid);
  const [leader, setLeader] = useState(initialCatch.leader);
  const [lure, setLure] = useState(initialCatch.lure);
  const [lureSize, setLureSize] = useState(initialCatch.lureSize);
  const [lureColor, setLureColor] = useState(initialCatch.lureColor);
  const [hookBrand, setHookBrand] = useState(initialCatch.hookBrand);
  const [hookSpec, setHookSpec] = useState(() =>
    hookTypeToSpec(initialCatch.hookType, initialCatch.hookSize)
  );
  const [knotBraidToLeader, setKnotBraidToLeader] = useState(
    initialCatch.knotBraidToLeader
  );
  const [knotLeaderToLure, setKnotLeaderToLure] = useState(
    initialCatch.knotLeaderToLure
  );
  const [catchAndRelease, setCatchAndRelease] = useState(
    initialCatch.catchAndRelease
  );
  const [activeDropdown, setActiveDropdown] = useState(null);
  const postId = String(post?.id || "");
  const mapPickerDraftRef = useRef({ postId });
  const getMapPickerDetail = useCallback(() => mapPickerDraftRef.current, []);
  const mediaStateRef = useRef(null);
  const appliedMapPickRef = useRef("");

  const locationState = useCatchLocationState({
    pickedCoordinates: normalizedPickedCoordinates,
    t,
    getCurrentMediaState: useCallback(() => mediaStateRef.current, []),
    initialCoordinates: initialCatch.coordinates,
    initialLocation: initialCatch.location,
    initialIsLocationPublic: initialCatch.isLocationPublic,
    initialLocationMode: initialCatch.coordinates ? "manual" : "none",
    draftExifDate: initialCatch.date,
    draftExifTime: initialCatch.time,
    setDraftExifDate: noop,
    setDraftExifTime: noop,
    mapPickerSource: "editPost",
    mapPickerDetail: getMapPickerDetail,
  });
  const {
    setCoordinates,
    setLocation,
    setIsLocationPublic,
    setLocationMode,
    setExifDate,
    setExifTime,
  } = locationState;

  useLayoutEffect(() => {
    setCoordinates(initialCatch.coordinates);
    setLocation(initialCatch.location);
    setIsLocationPublic(initialCatch.isLocationPublic);
    setLocationMode(initialCatch.coordinates ? "manual" : "none");
    setExifDate(initialCatch.date);
    setExifTime(initialCatch.time);
  }, [
    initialCatch.coordinates,
    initialCatch.date,
    initialCatch.isLocationPublic,
    initialCatch.location,
    initialCatch.time,
    setCoordinates,
    setExifDate,
    setExifTime,
    setIsLocationPublic,
    setLocation,
    setLocationMode,
  ]);

  useLayoutEffect(() => {
    if (!normalizedPickedCoordinates) return;

    const selectionKey = [
      postId,
      mapPickSelection?.selectionId || normalizedPickedCoordinates,
    ].join(":");
    if (appliedMapPickRef.current === selectionKey) return;

    appliedMapPickRef.current = selectionKey;
    setCoordinates(normalizedPickedCoordinates);
    setLocation("");
    setLocationMode("map");
  }, [
    mapPickSelection?.selectionId,
    normalizedPickedCoordinates,
    postId,
    setCoordinates,
    setLocation,
    setLocationMode,
  ]);

  useLayoutEffect(() => {
    setCaption(initialState.caption);
    setRating(initialCatch.rating);
    setShowFeedback(Boolean(initialCatch.feedback));
    setFeedback(initialCatch.feedback);
    setFishInput(initialCatch.fish);
    setFishSuggestions([]);
    setSize(initialCatch.size);
    setWeight(initialCatch.weight);
    setSizeUnit("cm");
    setSizeFeet("");
    setSizeInches("");
    setWeightUnit("kg");
    setWeightLb("");
    setWaterType(initialCatch.waterType || "all");
    setWayOfFishing(initialCatch.wayOfFishing);
    setTypeOfFishing(initialCatch.typeOfFishing);
    setStyle(initialCatch.style);
    setTechnique(initialCatch.technique);
    setRod(initialCatch.rod);
    setReel(initialCatch.reel);
    setBraid(initialCatch.braid);
    setLeader(initialCatch.leader);
    setLure(initialCatch.lure);
    setLureSize(initialCatch.lureSize);
    setLureColor(initialCatch.lureColor);
    setHookBrand(initialCatch.hookBrand);
    setHookSpec(hookTypeToSpec(initialCatch.hookType, initialCatch.hookSize));
    setKnotBraidToLeader(initialCatch.knotBraidToLeader);
    setKnotLeaderToLure(initialCatch.knotLeaderToLure);
    setCatchAndRelease(initialCatch.catchAndRelease);
  }, [initialCatch, initialState.caption]);

  useEffect(() => {
    setTypeOfFishingOptions(
      getTypeOfFishingOptionsForWay(resources.fishingMethods, wayOfFishing)
    );
  }, [resources.fishingMethods, wayOfFishing]);

  useEffect(() => {
    if (
      !wayOfFishing ||
      !typeOfFishing ||
      !resources.fishingMethods?.[wayOfFishing]
    ) {
      setStyleOptions([]);
      return;
    }

    const methods = resources.fishingMethods[wayOfFishing];
    const matchedKey = Object.keys(methods).find(
      (key) => key.toLowerCase().trim() === typeOfFishing.toLowerCase().trim()
    );

    setStyleOptions(
      matchedKey && Array.isArray(methods[matchedKey]) ? methods[matchedKey] : []
    );
  }, [resources.fishingMethods, typeOfFishing, wayOfFishing]);

  useEffect(() => {
    if (
      !wayOfFishing ||
      !typeOfFishing ||
      !style ||
      !resources.fishingMethods?.techniques?.[wayOfFishing]?.[typeOfFishing]?.[
        style
      ]
    ) {
      setTechniqueOptions([]);
      return;
    }

    const list =
      resources.fishingMethods.techniques[wayOfFishing][typeOfFishing][style];
    setTechniqueOptions(Array.isArray(list) ? list : []);
  }, [resources.fishingMethods, style, typeOfFishing, wayOfFishing]);

  const handleWayChange = useCallback((event) => {
    setWayOfFishing(event.target.value);
    setTypeOfFishing("");
    setStyle("");
    setTechnique("");
    setStyleOptions([]);
    setTechniqueOptions([]);
  }, []);

  const handleTypeOfFishingChange = useCallback((value) => {
    setTypeOfFishing(value);
    setStyle("");
    setTechnique("");
    setTechniqueOptions([]);
  }, []);

  const handleStyleChange = useCallback((value) => {
    setStyle(value);
    setTechnique("");
  }, []);

  const handleFishInputChange = useCallback(
    (event) => {
      const value = event.target.value;
      setFishInput(value);

      if (value.length > 1 && resources.fishList?.length) {
        setFishSuggestions(buildFishSpeciesSuggestions(resources.fishList, value));
      } else {
        setFishSuggestions([]);
      }
    },
    [resources.fishList]
  );

  const handleSelectFishSuggestion = useCallback((fish) => {
    setFishInput(fish.commonName);
    setFishSuggestions([]);
  }, []);

  const fishSpeciesErrorKey = getFishSpeciesValidationErrorKey(fishInput);

  const handleStarClick = useCallback((value) => {
    setRating(value);
    setShowFeedback(true);
  }, []);

  const buildPayloadFormState = useCallback(() => {
    const { hookType, hookSize } = specToHookFields(hookSpec);

    return {
      caption,
      catch: {
        fish: fishInput,
        size: buildSizeValue({ sizeUnit, size, sizeFeet, sizeInches }),
        weight: buildWeightValue({ weightUnit, weight, weightLb }),
        waterType: waterType === "all" ? "" : waterType,
        wayOfFishing,
        typeOfFishing,
        style,
        technique,
        rod,
        reel,
        braid,
        leader,
        lure,
        lureSize,
        lureColor,
        hookBrand,
        hookType,
        hookSize,
        knotBraidToLeader,
        knotLeaderToLure,
        catchAndRelease,
        rating,
        feedback,
        coordinates: normalizeCoords(locationState.coordinates),
        location: locationState.location || "",
        isLocationPublic: locationState.isLocationPublic,
        date: locationState.exifDate || "",
        time: locationState.exifTime || "",
      },
    };
  }, [
    braid,
    caption,
    catchAndRelease,
    feedback,
    fishInput,
    hookBrand,
    hookSpec,
    knotBraidToLeader,
    knotLeaderToLure,
    leader,
    locationState.coordinates,
    locationState.exifDate,
    locationState.exifTime,
    locationState.isLocationPublic,
    locationState.location,
    lure,
    lureColor,
    lureSize,
    rating,
    reel,
    rod,
    size,
    sizeFeet,
    sizeInches,
    sizeUnit,
    style,
    technique,
    typeOfFishing,
    waterType,
    wayOfFishing,
    weight,
    weightLb,
    weightUnit,
  ]);
  mapPickerDraftRef.current = {
    postId,
    draftFormState: buildPayloadFormState(),
  };

  return {
    t,
    ...resources,
    caption,
    setCaption,
    fishInput,
    fishSpeciesErrorKey,
    hasFishSpeciesError: Boolean(fishSpeciesErrorKey),
    fishSuggestions,
    handleFishInputChange,
    handleSelectFishSuggestion,
    size,
    setSize,
    weight,
    setWeight,
    sizeUnit,
    setSizeUnit,
    sizeFeet,
    setSizeFeet,
    sizeInches,
    setSizeInches,
    weightUnit,
    setWeightUnit,
    weightLb,
    setWeightLb,
    waterType,
    setWaterType,
    wayOfFishing,
    typeOfFishing,
    typeOfFishingOptions,
    setTypeOfFishing: handleTypeOfFishingChange,
    style,
    setStyle: handleStyleChange,
    styleOptions,
    technique,
    setTechnique,
    techniqueOptions,
    rod,
    setRod,
    reel,
    setReel,
    activeDropdown,
    setActiveDropdown,
    rodSuggestions:
      activeDropdown === "Rod" && rod
        ? getRodSuggestions(resources.rodDatabase, rod)
        : [],
    reelSuggestions:
      activeDropdown === "Reel" && reel
        ? getReelSuggestions(resources.reelDatabase, reel)
        : [],
    savedGearSets: [],
    savedGearSetId: "",
    isLoadingSavedGearSets: false,
    savedGearSetLoadError: "",
    handleSavedGearSetChange: noop,
    braid,
    setBraid,
    leader,
    setLeader,
    lure,
    setLure,
    lureSize,
    setLureSize,
    lureColor,
    setLureColor,
    hookBrand,
    setHookBrand,
    hookSpec,
    setHookSpec,
    knotBraidToLeader,
    setKnotBraidToLeader,
    knotLeaderToLure,
    setKnotLeaderToLure,
    catchAndRelease,
    setCatchAndRelease,
    rating,
    feedback,
    setFeedback,
    showFeedback,
    handleWayChange,
    handleStarClick,
    isLureFishingType,
    coordinates: locationState.coordinates,
    isLocationPublic: locationState.isLocationPublic,
    locationMode: locationState.locationMode,
    locationHint: locationState.locationHint,
    exifDate: locationState.exifDate,
    exifTime: locationState.exifTime,
    handleUseGps: locationState.handleUseGps,
    handlePickFromMap: locationState.handlePickFromMap,
    handleCoordinatesChange: locationState.handleCoordinatesChange,
    handleCoordinatesBlur: locationState.handleCoordinatesBlur,
    handleSetLocationPublic: locationState.handleSetLocationPublic,
    handleSetLocationPrivate: locationState.handleSetLocationPrivate,
    handleClearCoordinates: locationState.handleClearCoordinates,
    setLocationMode: locationState.setLocationMode,
    showLocationActionsWhenActive: true,
    handleManualDateChange: locationState.handleManualDateChange,
    handleManualTimeChange: locationState.handleManualTimeChange,
    buildPayloadFormState,
  };
}
